import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Button, Field, Input, toast } from '@/components/ui';
import { AuthLayout } from '@/features/auth/auth-layout';
import { authClient, unwrap } from '@/lib/auth';

export const Route = createFileRoute('/reset-password')({
  validateSearch: (search: Record<string, unknown>): { token?: string; error?: string } => ({
    ...(typeof search.token === 'string' ? { token: search.token } : {}),
    ...(typeof search.error === 'string' ? { error: search.error } : {}),
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { token, error: linkError } = Route.useSearch();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) return setError('Use at least 8 characters');
    setError(null);
    setPending(true);
    try {
      unwrap(await authClient.resetPassword({ newPassword: password, token: token! }));
      toast.success('Password updated', 'Sign in with your new password.');
      await navigate({ to: '/login' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setPending(false);
    }
  };

  const invalid = !token || Boolean(linkError);
  return (
    <AuthLayout
      title="Choose a new password"
      footer={
        <Link to="/login" className="font-medium text-accent-text hover:underline">
          Back to sign in
        </Link>
      }
    >
      {invalid ? (
        <div className="text-center" role="alert">
          <p className="font-medium">This reset link is invalid or has expired</p>
          <p className="mt-1 text-sm text-fg-muted">Request a new one and try again.</p>
          <Button asChild variant="primary" className="mt-4">
            <Link to="/forgot-password">Request a new link</Link>
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3.5" noValidate>
          <Field label="New password" htmlFor="password" hint="At least 8 characters" error={error}>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-9"
            />
          </Field>
          <Button type="submit" variant="primary" size="lg" className="w-full" loading={pending}>
            Update password
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
